/**
 * LeadsQuickAddFab — Oct 2026 · R291
 *
 * Mobile-first floating "Add lead" button that drops a 3-field capture
 * form so sales reps on the go can jot down a prospect in seconds
 * without wading through the full Lead dialog. On tablet+ the full
 * "+ New lead" button in the toolbar continues to serve power users;
 * this FAB is `md:hidden` and only renders when the user has
 * permission to create leads.
 *
 * Props:
 *   wsId         — active workspace id (needed for the create endpoint)
 *   sources      — array of common source strings for the dropdown
 *   onCreated    — called with the created lead; parent prepends to list
 *   onOpenFullForm — called with the partial draft when the user taps
 *                    "Add more details" (opens LeadDialog pre-filled)
 *   visible      — hide the FAB entirely (e.g. on the Dashboard subtab)
 */
import { useState } from "react";
import api from "@/lib/api";
import { apiErrorMessage } from "@/lib/apiError";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "@/components/ui/dialog";
import {
  Select, SelectTrigger, SelectValue, SelectContent, SelectItem,
} from "@/components/ui/select";
import { Plus, Loader2, UserPlus } from "lucide-react";

export default function LeadsQuickAddFab({
  wsId, sources = [], onCreated, onOpenFullForm, visible = true,
}) {
  const [open, setOpen] = useState(false);
  const [name, setName] = useState("");
  const [phone, setPhone] = useState("");
  const [source, setSource] = useState("other");
  const [busy, setBusy] = useState(false);

  if (!visible) return null;

  const reset = () => { setName(""); setPhone(""); setSource("other"); };
  const close = () => { setOpen(false); reset(); };

  const buildPayload = () => {
    const payload = { name: name.trim(), source };
    const p = phone.trim();
    if (p) payload.phone = p;
    return payload;
  };

  const submit = async () => {
    if (!name.trim()) {
      toast.error("Name is required");
      return;
    }
    if (!wsId) {
      toast.error("No active workspace");
      return;
    }
    setBusy(true);
    try {
      const r = await api.post(`/workspaces/${wsId}/leads`, buildPayload());
      toast.success("Lead added");
      onCreated?.(r?.data || null);
      close();
    } catch (e) {
      toast.error(apiErrorMessage(e, "Could not add lead"));
    } finally {
      setBusy(false);
    }
  };

  const openFull = () => {
    const draft = buildPayload();
    // Allow parent to merge the 3 fields into a brand-new LeadDialog
    // open — avoids the sales rep retyping everything they already
    // entered on the quick form.
    onOpenFullForm?.(draft);
    close();
  };

  return (
    <>
      {/* R291 · The floating "+" sits above the mobile sticky-bars
          (bottom nav, intercom, etc.) via a conservative z-index.
          `pb-safe` + inline inset keep the button clear of iOS home
          indicators. */}
      <button
        type="button"
        onClick={() => setOpen(true)}
        aria-label="Add a lead"
        data-testid="leads-mobile-fab"
        className="md:hidden fixed z-40 bottom-5 right-5 h-14 w-14 rounded-full bg-primary text-primary-foreground shadow-xl shadow-primary/30 flex items-center justify-center active:scale-95 transition-transform"
        style={{ paddingBottom: "env(safe-area-inset-bottom, 0px)" }}
      >
        <Plus className="w-6 h-6" strokeWidth={2.5} />
      </button>

      <Dialog open={open} onOpenChange={(o) => (o ? setOpen(true) : close())}>
        <DialogContent className="sm:max-w-md rounded-none" data-testid="leads-quickadd-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <UserPlus className="w-4 h-4" /> Quick add lead
            </DialogTitle>
            <DialogDescription className="text-[11px]">
              Just the essentials — you can enrich later. Tap <b>Add more details</b>
              to open the full form with these fields pre-filled.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div>
              <Label htmlFor="qa-name" className="text-xs">Name <span className="text-rose-600">*</span></Label>
              <Input
                id="qa-name"
                autoFocus
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder="e.g. Priya Sharma"
                className="rounded-none h-10 text-base" /* bigger on mobile */
                data-testid="leads-quickadd-name"
                autoComplete="off"
              />
            </div>
            <div>
              <Label htmlFor="qa-phone" className="text-xs">Phone (optional)</Label>
              <Input
                id="qa-phone"
                type="tel"
                inputMode="tel"
                value={phone}
                onChange={(e) => setPhone(e.target.value)}
                placeholder="+91 98765 43210"
                className="rounded-none h-10 text-base"
                data-testid="leads-quickadd-phone"
                autoComplete="tel"
              />
            </div>
            <div>
              <Label className="text-xs">Where from?</Label>
              <Select value={source} onValueChange={setSource}>
                <SelectTrigger className="h-10 rounded-none text-sm" data-testid="leads-quickadd-source">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {(sources.length ? sources : ["other"]).map((s) => (
                    <SelectItem key={s} value={s} className="capitalize">{s}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <p className="text-[11px] text-muted-foreground pt-1">
              Status defaults to <b>New</b>. You stay the owner. Change either from the full form.
            </p>
          </div>
          <DialogFooter className="flex-col-reverse sm:flex-row gap-2">
            <Button
              type="button"
              variant="outline"
              className="rounded-none w-full sm:w-auto"
              onClick={openFull}
              disabled={busy}
              data-testid="leads-quickadd-full"
            >
              Add more details
            </Button>
            <Button
              type="button"
              className="rounded-none w-full sm:w-auto"
              onClick={submit}
              disabled={busy || !name.trim()}
              data-testid="leads-quickadd-submit"
            >
              {busy ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Plus className="w-4 h-4 mr-2" />}
              Add lead
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
